import _extends from "@babel/runtime/helpers/extends";
import React, { Component, Children, cloneElement, createRef } from 'react';
import PropTypes from 'prop-types';
import { mdiChevronDown, mdiChevronUp, mdiClose } from '@mdi/js';
import { pick } from 'lodash-es';
import { Wrapper as AriaWrapper, Button as AriaButton, Menu as AriaMenu, closeMenu } from 'react-aria-menubutton';
import URCButton from '../Button';
import * as Emphasis from '../Button/emphasis';
import { withFormMessage } from '../FormMessage';
import * as Sizes from '../TextInput/sizes';
import { autofocusLegacyComponentDidMount } from '../hooks/useAutofocus';
import { getLabelId } from '../utils/aria';
import Text from '../Typography/Text';
import SelectItem from './SelectItem';
import { Button, DropdownButton, Label, SelectedValue, MenuContainer, Arrow, Container, Spinner, ChipsContainer, SelectChip, SelectChipContent, SelectChipText, ChipRemoveIcon } from './styles';

// TODO: Accessibility improvements needed:
// - Review and enhance keyboard navigation support for multiple selection
// - Consider adding aria-selected state management for keyboard interactions
// - Test with screen readers to ensure proper announcement of selected items
// - Consider adding aria-live regions for dynamic updates
// - Review and enhance focus management between chips and dropdown

class Select extends Component {
  static propTypes = {
    autofocus: PropTypes.bool,
    className: PropTypes.string,
    disabled: PropTypes.bool,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
    children: PropTypes.node.isRequired,
    onChange: PropTypes.func,
    onMenuToggle: PropTypes.func,
    label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    floatingActive: PropTypes.bool,
    emphasis: PropTypes.oneOf(Object.keys(Emphasis)),
    destructive: PropTypes.bool,
    size: PropTypes.string,
    inline: PropTypes.bool,
    error: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    buttonProps: PropTypes.object,
    // eslint-disable-line react/forbid-prop-types
    selectedValueId: PropTypes.string,
    renderTrigger: PropTypes.func,
    dropdown: PropTypes.bool,
    display: PropTypes.string,
    direction: PropTypes.oneOf(['up', 'down']),
    alignment: PropTypes.oneOf(['left', 'right', 'leftside', 'rightside']),
    menuContainerClassName: PropTypes.string,
    menuMargin: PropTypes.string,
    menuItemSize: PropTypes.oneOf(['small', 'normal']),
    menuWidth: PropTypes.string,
    menuMaxHeight: PropTypes.string,
    searchable: PropTypes.bool,
    initialValue: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
    showRequired: PropTypes.bool,
    loading: PropTypes.bool,
    multiple: PropTypes.bool,
    renderChip: PropTypes.func,
    chipProps: PropTypes.object,
    // eslint-disable-line react/forbid-prop-types
    menuProps: PropTypes.object,
    // eslint-disable-line react/forbid-prop-types
    placeholder: PropTypes.string
  };
  static defaultProps = {
    autofocus: undefined,
    className: undefined,
    disabled: false,
    value: undefined,
    onChange: null,
    onMenuToggle: null,
    floatingActive: false,
    emphasis: undefined,
    destructive: undefined,
    size: Sizes.normal,
    inline: false,
    label: undefined,
    error: false,
    buttonProps: {},
    selectedValueId: undefined,
    renderTrigger: undefined,
    dropdown: false,
    display: 'block',
    direction: 'down',
    alignment: 'left',
    menuContainerClassName: undefined,
    menuMargin: undefined,
    menuItemSize: 'normal',
    menuWidth: '100%',
    menuMaxHeight: '330px',
    searchable: false,
    initialValue: undefined,
    showRequired: false,
    loading: false,
    multiple: false,
    renderChip: undefined,
    chipProps: {},
    placeholder: undefined
  };
  static mapFieldValue = (e, selected) => selected;
  static injectMessages = true;
  static injectFormProps = {};
  constructor(props) {
    super(props);

    // default selected value
    const {
      value = undefined,
      initialValue,
      multiple
    } = props;

    // dom ids used for aria
    this.id = this.props.id || getLabelId('select');
    this.labelId = getLabelId('select-label');
    this.listId = getLabelId('select-option-list');
    this.itemId = getLabelId('select-option');
    this.isControlled = value !== undefined;
    this.buttonRef = /*#__PURE__*/createRef();
    const defaultValue = multiple ? [] : undefined;
    this.state = {
      // menu state
      isOpen: false,
      // focused menu item
      activeDescendant: null,
      // selected menu item(s)
      value: this.isControlled ? defaultValue : initialValue || defaultValue
    };
  }
  componentDidMount() {
    autofocusLegacyComponentDidMount({
      ref: this.buttonRef,
      autofocus: this.props.autofocus
    });
  }
  componentDidUpdate(prevProps) {
    if (this.props.loading && !prevProps.loading) {
      closeMenu(this.id);
    }
  }
  getValue() {
    const {
      value: propValue,
      multiple
    } = this.props;
    const {
      value: stateValue
    } = this.state;
    const currentValue = this.isControlled ? propValue : stateValue;
    if (multiple) {
      return Array.isArray(currentValue) ? currentValue : [];
    }
    return currentValue;
  }
  getSelected() {
    const {
      children,
      multiple
    } = this.props;
    const currentValue = this.getValue();
    if (multiple) {
      const selectedValues = Array.isArray(currentValue) ? currentValue : [];
      return React.Children.toArray(children).filter(child => selectedValues.includes(child.props.value)).map(child => {
        const {
          selectedLabel,
          label,
          text,
          value
        } = child.props;
        return {
          selectedLabel,
          label,
          text,
          value
        };
      });
    }
    const selectedChild = React.Children.toArray(children).filter(child => child.props.value === currentValue);
    if (selectedChild.length) {
      const {
        selectedLabel,
        label,
        text,
        value
      } = selectedChild[0].props;
      return {
        selectedLabel,
        label,
        text,
        value
      };
    }
    return {};
  }
  handleSelection = (value, e) => {
    const {
      onChange,
      multiple
    } = this.props;
    let newValue;
    if (multiple) {
      const currentValues = this.getValue();
      if (currentValues.includes(value)) {
        // Remove from selection
        newValue = currentValues.filter(v => v !== value);
      } else {
        // Add to selection
        newValue = [...currentValues, value];
      }
    } else {
      newValue = value;
      // Close menu for single select
      closeMenu(this.id);
    }
    if (onChange) {
      onChange(e, newValue);
    }
    this.setState({
      value: newValue
    });
  };
  handleChipRemove = (valueToRemove, e) => {
    e.stopPropagation();
    const {
      onChange
    } = this.props;
    const currentValues = this.getValue();
    const newValue = currentValues.filter(v => v !== valueToRemove);
    if (onChange) {
      onChange(e, newValue);
    }
    this.setState({
      value: newValue
    });
  };
  handleToggle = ({
    isOpen
  }, e) => {
    this.setState({
      isOpen,
      activeDescendant: null
    });
    const {
      onMenuToggle
    } = this.props;
    if (typeof onMenuToggle === 'function') {
      onMenuToggle({
        opened: isOpen
      }, e);
    }
  };
  handleFocus = event => {
    if (event.target) {
      this.setState({
        activeDescendant: event.target.id
      });
    }
  };
  handleLoading = event => {
    if (this.props.loading) {
      event.stopPropagation();
    }
  };
  renderSpinner() {
    const {
      size,
      dropdown
    } = this.props;
    const dimensions = {
      xsmall: '0.75rem',
      small: '1rem',
      normal: '1.25rem',
      large: '1.5rem'
    };
    const dimension = dimensions[size] ?? dimensions.normal;
    return /*#__PURE__*/React.createElement(Spinner, {
      dropdown: true,
      size: size,
      width: dimension,
      height: dimension,
      color: dropdown ? 'backgroundInactive' : 'content',
      role: "presentation",
      "aria-label": null,
      "data-auto": "spinner"
    });
  }
  renderArrow() {
    const {
      isOpen
    } = this.state;
    const {
      disabled,
      direction,
      dropdown,
      inline
    } = this.props;
    let color = disabled ? 'contentDisabled' : 'content';
    if (dropdown) {
      color = 'backgroundInactive';
    }
    const rotate = direction === 'up' ? 180 : -180;
    return /*#__PURE__*/React.createElement(Arrow, {
      "aria-hidden": true,
      icon: direction === 'up' ? mdiChevronUp : mdiChevronDown,
      color: color,
      rotate: isOpen ? rotate : 0,
      size: "1.5rem",
      "data-inline": dropdown ? true : inline,
      "data-dropdown": dropdown
    });
  }
  renderChips() {
    const {
      multiple,
      renderChip,
      chipProps
    } = this.props;
    if (!multiple) return null;
    const selectedItems = this.getSelected();
    if (!selectedItems.length) return null;
    return /*#__PURE__*/React.createElement(ChipsContainer, {
      "data-auto": "chips-container"
    }, selectedItems.map(item => {
      const chipContent = item.selectedLabel || item.label || item.text || item.value;
      if (renderChip) {
        return renderChip(item, this.handleChipRemove);
      }
      return /*#__PURE__*/React.createElement(SelectChip, _extends({
        key: item.value,
        "data-auto": "chip",
        "data-value": item.value,
        "data-selected": item.value,
        "aria-selected": "true"
      }, chipProps, {
        onClick: e => {
          e.stopPropagation();
          // Allow custom chip click handling
          if (chipProps && chipProps.onClick) {
            chipProps.onClick(e);
          }
        }
      }), /*#__PURE__*/React.createElement(SelectChipContent, null, /*#__PURE__*/React.createElement(SelectChipText, null, chipContent), /*#__PURE__*/React.createElement(ChipRemoveIcon, {
        icon: mdiClose,
        size: "1rem",
        color: "content",
        onClick: e => this.handleChipRemove(item.value, e),
        "data-auto": "chip-remove",
        "data-value": item.value
      })));
    }));
  }
  renderButton = () => {
    const {
      buttonProps,
      className,
      disabled,
      dropdown,
      error,
      floatingActive,
      inline,
      label,
      renderTrigger,
      selectedValueId,
      showLabel = label !== undefined,
      showSelectedLabel = label !== undefined,
      size,
      emphasis,
      destructive,
      showRequired,
      loading,
      multiple,
      placeholder
    } = this.props;
    const {
      isOpen,
      activeDescendant
    } = this.state;
    const loadingProps = loading ? {
      'data-loading': true,
      'aria-busy': true,
      onClick: this.handleLoading,
      onKeyDown: this.handleLoading
    } : {};
    const ariaProps = pick(this.props, ['aria-invalid', 'aria-describedby', 'aria-required']);
    const containerProps = {
      'data-error': error
    };
    const sharedButtonProps = {
      ref: el => {
        // AriaMenuButtonButton expects ref to be a function
        this.buttonRef.current = el;
      },
      disabled,
      'data-error': error,
      'data-opened': isOpen,
      'aria-haspopup': 'listbox',
      'aria-controls': this.listId,
      'aria-activedescendant': activeDescendant,
      'aria-labelledby': this.labelId,
      role: 'combobox',
      ...ariaProps,
      ...loadingProps
    };
    const spinner = this.renderSpinner();
    const arrow = this.renderArrow();
    const icon = loading && !disabled ? spinner : arrow;
    if (renderTrigger) {
      return /*#__PURE__*/React.createElement(AriaButton, _extends({}, sharedButtonProps, {
        style: {
          cursor: 'pointer'
        }
      }), renderTrigger({
        disabled,
        error,
        isOpen
      }));
    }
    if (dropdown) {
      return /*#__PURE__*/React.createElement(DropdownButton, _extends({}, sharedButtonProps, {
        tag: URCButton,
        className: className,
        disabled: disabled,
        emphasis: emphasis,
        destructive: destructive,
        size: size,
        buttonProps: {
          ...buttonProps,
          disabled
        }
      }), label, icon);
    }
    const selectedValue = this.getSelected();
    const hasSelectedValue = multiple ? selectedValue.length > 0 : selectedValue.value || selectedValue.value === 0;
    const shouldShowFloatingLabel = hasSelectedValue || floatingActive;
    return /*#__PURE__*/React.createElement(Container, containerProps, /*#__PURE__*/React.createElement(Button, _extends({}, buttonProps, sharedButtonProps, {
      className: className,
      tag: "div",
      as: AriaButton,
      "data-size": Sizes.getRef(size),
      "data-inline": inline,
      "data-multiple": multiple
    }), multiple ? /*#__PURE__*/React.createElement(React.Fragment, null, this.renderChips(), !hasSelectedValue && placeholder && /*#__PURE__*/React.createElement(SelectedValue, {
      "data-size": Sizes.getRef(size),
      id: selectedValueId,
      "data-placeholder": true,
      $showLabel: showLabel,
      $showSelectedLabel: showSelectedLabel
    }, placeholder), hasSelectedValue && /*#__PURE__*/React.createElement(SelectedValue, {
      "data-size": Sizes.getRef(size),
      $showLabel: showLabel,
      $showSelectedLabel: showSelectedLabel,
      style: {
        display: 'none'
      },
      "aria-hidden": "true"
    })) : shouldShowFloatingLabel && /*#__PURE__*/React.createElement(SelectedValue, {
      "data-size": Sizes.getRef(size),
      id: selectedValueId,
      "data-selected": selectedValue.value,
      title: selectedValue.text || selectedValue.label,
      $showLabel: showLabel,
      $showSelectedLabel: showSelectedLabel
    }, selectedValue.selectedLabel || selectedValue.label), showLabel && /*#__PURE__*/React.createElement(Label, {
      id: this.labelId,
      "data-size": Sizes.getRef(size),
      "aria-hidden": "true",
      $disabled: disabled
    }, label, showRequired && /*#__PURE__*/React.createElement(Text, {
      color: "semanticError"
    }, "*")), icon));
  };
  injectRole = (node, key) => {
    if (! /*#__PURE__*/React.isValidElement(node)) return node;
    const {
      multiple,
      searchable
    } = this.props;
    const currentValue = this.getValue();
    const id = `${this.itemId}-${key}`;
    const role = 'option';

    // selected item
    const isSelected = multiple ? currentValue.includes(node.props.value) : node.props.value === currentValue;

    // we can add a callback to transform nodes into text to enable the keyboard search
    const text = searchable ? node.props.value : null;

    // Add checkmark for multi-select
    const checkmark = multiple ? true : node.props.checkmark;
    return /*#__PURE__*/cloneElement(node, {
      id,
      role,
      isSelected,
      text,
      checkmark,
      'aria-selected': isSelected
    });
  };
  render() {
    const {
      children,
      direction,
      display,
      alignment,
      menuContainerClassName,
      menuMargin,
      menuItemSize,
      menuWidth,
      menuMaxHeight,
      menuProps
    } = this.props;
    return /*#__PURE__*/React.createElement(AriaWrapper, {
      id: this.id,
      onFocus: this.handleFocus,
      onSelection: this.handleSelection,
      onMenuToggle: this.handleToggle,
      style: {
        position: 'relative',
        textAlign: alignment,
        display
      }
    }, this.renderButton(), /*#__PURE__*/React.createElement(AriaMenu, _extends({
      id: this.listId,
      role: "listbox",
      "aria-multiselectable": this.props.multiple
    }, menuProps), /*#__PURE__*/React.createElement(MenuContainer, {
      className: menuContainerClassName,
      "data-max-height": menuMaxHeight,
      "data-direction": direction,
      "data-alignment": alignment,
      "data-margin": menuMargin,
      "data-size": menuItemSize,
      style: {
        width: menuWidth
      }
    }, Children.map(children, this.injectRole))));
  }
}
export default Select;
export { SelectItem };
export const SelectWithMessage = withFormMessage()(Select);
SelectWithMessage.displayName = 'SelectWithMessage';
//# sourceMappingURL=index.js.map